/**
 * About Page
 *
 * Public landing page describing the team and mission, with SEO metadata and
 * theme toggle support via `SimpleLandingHeader`.
 */
import React, { useEffect } from 'react';
import SEOHead from '@/components/shared/SEOHead';
import { SimpleLandingHeader } from '@/components/landing/shared/SimpleLandingHeader/SimpleLandingHeader';
import useDarkMode from '@/hooks/useDarkMode';
import { Footer } from '@/components/shared/Footer';

export default function About() {
  const isDark = useDarkMode();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleTheme = () => {
    if (isDark) {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    } else {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    }
  };

  // Structured data for the About page
  const aboutStructuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "AboutPage",
        "@id": "https://advocacylab.com/about#page",
        "url": "https://advocacylab.com/about",
        "name": "About Advocacy Lab",
        "description": "Learn about Advocacy Lab's mission to democratize political creative design and meet our experienced team of political strategists and technologists.",
        "mainEntity": {
          "@type": "Organization",
          "@id": "https://advocacylab.com/#organization",
          "name": "Advocacy Lab",
          "description": "A platform dedicated to democratizing political creative design for campaigns and advocacy groups.",
          "url": "https://advocacylab.com",
          "foundingDate": "2023",
          "industry": "Political Technology",
          "mission": "To democratize access to professional-quality campaign materials so that great ideas can win, regardless of budget",
          "parentOrganization": {
            "@type": "Organization",
            "name": "FullPAC",
            "url": "https://gotv.com"
          },
          "founder": [
            {
              "@type": "Person",
              "name": "Kevin Rose",
              "jobTitle": "Founder",
              "description": "Political strategist and creative with seven years of experience in legislative affairs and campaign media. Former White House intern.",
              "worksFor": {
                "@id": "https://advocacylab.com/#organization"
              }
            },
            {
              "@type": "Person", 
              "name": "Karl Brycz",
              "jobTitle": "Technical Cofounder",
              "description": "Seasoned technologist with expertise in building scalable digital platforms for advocacy and campaign work.",
              "worksFor": {
                "@id": "https://advocacylab.com/#organization"
              }
            }
          ],
          "employee": [
            {
              "@type": "Person",
              "name": "Kevin Rose",
              "jobTitle": "Founder"
            },
            {
              "@type": "Person",
              "name": "Karl Brycz", 
              "jobTitle": "Technical Cofounder"
            }
          ],
          "knowsAbout": [
            "Political Campaign Strategy",
            "Creative Design",
            "Digital Marketing",
            "Advocacy Communications",
            "Campaign Technology"
          ]
        }
      }
    ]
  };

  const breadcrumbs = [
    { name: "Home", url: "https://advocacylab.com" },
    { name: "About", url: "https://advocacylab.com/about" }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-white">
      <SEOHead 
        pageType="about"
        structuredDataOverride={aboutStructuredData}
        breadcrumbs={breadcrumbs}
      />
      
      {/* Header */}
      <SimpleLandingHeader 
        isDark={isDark} 
        toggleTheme={toggleTheme} 
        backLink="/"
        showLogo={true}
      />

      {/* About Advocacy Lab - Overview */}
      <section className="bg-white dark:bg-gray-900 pt-12 pb-6 px-4">
        <div className="mx-auto max-w-5xl">
          <div className="text-center mb-8">
            <h2 className="text-base font-semibold leading-7 text-purple-600 dark:text-purple-400">About Advocacy Lab</h2>
            <p className="mt-2 text-4xl font-semibold tracking-tight leading-tight text-balance text-gray-900 dark:text-white sm:text-5xl sm:leading-tight">
              Who We Are
            </p>
          </div>
          <div className="prose prose-lg dark:prose-invert max-w-4xl mx-auto px-4">
            <p className="text-gray-700 dark:text-gray-300 text-lg mb-10 text-justify">
              Advocacy Lab is now part of FullPAC, alongside RoboCent, and operates within the FullPAC family at <a href="https://gotv.com" target="_blank" rel="noopener noreferrer" className="text-purple-600 dark:text-purple-400 hover:underline">GOTV.com</a>. We were founded to democratize access to professional-quality campaign materials so that great ideas can win regardless of budget. As veterans of political campaigns and design experts, we remain driven by innovation, accessibility, and excellence—bringing cutting-edge tools and templates to organizations of all sizes. As part of FullPAC, we pair Advocacy Lab's creative toolkit with RoboCent's proven political outreach platform, which specializes in peer-to-peer texting and SMS campaigns, helping campaigns and advocacy groups reach voters at scale with personalized messaging. Together, we help you go from idea to action faster—whether you need compelling creative assets or powerful grassroots organizing tools. We measure our success by the positive change our users create, and we never compromise on quality.
            </p>
          </div>
        </div>
      </section>
      
      {/* Footer with added padding */}
      <div className="pb-16">
        <Footer />
      </div>
    </div>
  );
}